import { FEATURE_FLAGS } from "@repo/shared";
import type { CostOverrideTarget } from "@repo/shared/types";
import { type ReactNode, useState } from "react";
import { apiErrorMessage } from "@/lib/api/_client";
import { useFeatureFlag } from "@/lib/hooks/use-feature-flags";
import {
  useClearCostOverride,
  useCostOverrides,
  useSetCostOverride,
} from "@/lib/hooks/use-flow-instances/use-cost-overrides";
import { useT } from "@/lib/i18n/use-t";
import { CostOverrideDialog, type CostOverrideDialogTarget } from "./cost-override-dialog";
import { T } from "./use-cost-override-editor.i18n";

export type CostOverrideEdit = { request: CostOverrideTarget; dialog: CostOverrideDialogTarget };

const sameTarget = (a: CostOverrideTarget, b: CostOverrideTarget) =>
  a.kind === "product_cost" && b.kind === "product_cost"
    ? a.productId === b.productId && a.costTypeId === b.costTypeId
    : a.kind === "portfolio_cost" && b.kind === "portfolio_cost"
      ? a.portfolioCostId === b.portfolioCostId
      : false;

/**
 * QL-20 — the advisor's fee override editing for one flow instance: `edit`
 * opens the dialog for a fee, `dialog` is the element to render once.
 * `canReset` is false on a locked instance and without an instance (client
 * flow); the widgets then show no edit affordance. `canEdit` also needs the
 * tenant's cost_overrides feature: without it an existing override can still
 * be removed (an override never outlives the ability to remove it), but no new
 * one saved. Saving still goes through every server-side rule.
 */
export function useCostOverrideEditor(
  instanceId: string | null,
  options: { locked: boolean },
): {
  canEdit: boolean;
  canReset: boolean;
  edit: (edit: CostOverrideEdit) => void;
  dialog: ReactNode;
} {
  const t = useT(T);
  const canReset = !!instanceId && !options.locked;
  // The flag check is an advisor-session call; the client flow has no instance here.
  const { enabled } = useFeatureFlag(FEATURE_FLAGS.COST_OVERRIDES, { enabled: canReset });
  const canEdit = canReset && enabled;
  const [current, setCurrent] = useState<CostOverrideEdit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { overrides } = useCostOverrides(canReset ? instanceId : null);
  const setOverride = useSetCostOverride(instanceId ?? "");
  const clearOverride = useClearCostOverride(instanceId ?? "");

  const close = () => {
    setCurrent(null);
    setError(null);
  };

  const run = async (action: () => Promise<unknown>) => {
    try {
      setError(null);
      await action();
      close();
    } catch (err) {
      setError(apiErrorMessage(err, t.saveFailed));
    }
  };

  const onSave = async (value: number, note: string | null) => {
    if (!current) return;
    await run(() => setOverride.mutateAsync({ ...current.request, value, note }));
  };

  const onReset = async () => {
    if (!current) return;
    const existing = overrides.find((o) => sameTarget(o, current.request));
    if (!existing) {
      // The override list hasn't loaded (or failed): don't pretend the reset happened.
      setError(t.resetFailed);
      return;
    }
    await run(() => clearOverride.mutateAsync(existing.id));
  };

  const isPending = setOverride.isPending || clearOverride.isPending;

  const edit = (next: CostOverrideEdit) => {
    setError(null);
    setCurrent(next);
  };

  const dialog = canReset ? (
    <CostOverrideDialog
      target={current?.dialog ?? null}
      // A save in flight finishes into this dialog; it closes itself then.
      onClose={() => !isPending && close()}
      onSave={onSave}
      onReset={onReset}
      isPending={isPending}
      error={error}
    />
  ) : null;

  return { canEdit, canReset, edit, dialog };
}
